import isEqual from 'lodash.isequal';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { Layer, Source } from 'react-map-gl';
import { MapMouseEvent, SymbolLayerSpecification } from 'mapbox-gl';
import { type Feature, type Point, featureCollection, point } from '@turf/helpers';
import { useValueRef } from '@alltrails/core';
import logCustomRouteUpdated from '@alltrails/amplitude/events/logCustomRouteUpdated';
import logCustomRouteContextualEditMenuViewed from '@alltrails/amplitude/events/logCustomRouteContextualEditMenuViewed';
import { useAppDispatch, useAppSelector } from '../../../redux';
import useMap from '../../../hooks/useMap';
import type { MarkerProperties } from '../../../types/CustomRoute';
import { customRouteMarkerDragId, customRouteMarkersId, defaultSymbolLayerLayoutProps, getLayerBeforeId } from '../../../utils/layers';
import startSvg from '../../../utils/svgDefinitions/startSvg';
import endSvg from '../../../utils/svgDefinitions/endSvg';
import startEndSvg from '../../../utils/svgDefinitions/startEndSvg';
import handleSvg from '../../../utils/svgDefinitions/handleSvg';
import { MapLayerInteractionsContext } from '../../MapLayerInteractionsProvider';
import { DrawControlsContext } from '../DrawControls/DrawControlsProvider';
import { setActiveSegmentId, setSelectedHandleIndex } from '../../../redux/customRoute';
import ContextMenu from './ContextMenu';
import PulsingMarker from './PulsingMarker';

export const SOURCE_ID = 'custom-route-markers-source';
export const DRAG_SOURCE_ID = 'custom-route-marker-drag-source';
export const MARKER_PULSE_SOURCE_ID = 'marker-pulse';
export const MARKER_PULSE_LAYER_ID = 'marker-pulse-layer';
const IS_START_EXPRESSION = ['==', ['get', 'isStart'], true];
const IS_END_EXPRESSION = ['==', ['get', 'isEnd'], true];

// Layout is shared between all markers
const layout: SymbolLayerSpecification['layout'] = {
  ...defaultSymbolLayerLayoutProps,
  'symbol-sort-key': ['case', IS_START_EXPRESSION, 0, IS_END_EXPRESSION, 2, 1],
  'icon-image': [
    'case',
    ['all', IS_START_EXPRESSION, IS_END_EXPRESSION],
    startEndSvg.name,
    IS_START_EXPRESSION,
    startSvg.name,
    IS_END_EXPRESSION,
    endSvg.name,
    handleSvg.name
  ],
  'icon-allow-overlap': true
};

const getMarkerId = (segmentId: number, markerIndex: number) => {
  return `segment-${segmentId}-marker-${markerIndex}`;
};

const Markers = () => {
  const map = useMap();
  const dispatch = useAppDispatch();
  const beforeId = getLayerBeforeId(map, customRouteMarkersId);
  const { isEditing, activeSegmentId, selectedHandleIndex, allSegments, isEditingWaypoint } = useAppSelector(state => ({
    isEditing: state.customRoute.isEditing,
    activeSegmentId: state.customRoute.activeSegmentId,
    selectedHandleIndex: state.customRoute.selectedHandleIndex,
    allSegments: state.customRoute.route.segments,
    isEditingWaypoint: state.customRoute.isEditingWaypoint
  }));
  const selectedPoint = useMemo(() => {
    if (activeSegmentId === undefined || selectedHandleIndex === undefined) {
      return undefined;
    }

    const segment = allSegments.find(segment => segment.id === activeSegmentId);
    if (!segment || selectedHandleIndex > segment.paths.length) {
      return undefined;
    }

    return selectedHandleIndex === segment.paths.length
      ? segment.paths[segment.paths.length - 1].points[segment.paths[segment.paths.length - 1].points.length - 1]
      : segment.paths[selectedHandleIndex].points[0];
  }, [activeSegmentId, allSegments, selectedHandleIndex]);

  const selectedHandleIndexRef = useValueRef(selectedHandleIndex);

  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);
  const { pendingHandle, dragMarker, isContextMenuOpen, isContextMenuOpenRef, setIsContextMenuOpen } = useContext(DrawControlsContext);

  // Layer behaves unexpectedly when passing undefined for filter so memoize a props object containing the filter
  const filter = useMemo(() => (dragMarker ? { filter: ['!=', ['get', 'id'], dragMarker.id] } : {}), [dragMarker]);

  const dragData = useMemo(() => {
    if (!dragMarker) {
      return featureCollection<Point, MarkerProperties>([]);
    }

    const points: Feature<Point, MarkerProperties>[] = [];
    const { segmentId, endPathIndex, isStart, isEnd, latitude, longitude } = dragMarker;

    if (isStart && isEnd) {
      // Default combined start/end marker selection is the endpoint so if a combined marker is being dragged while another handle is selected we're really dragging the end handle
      // We only drag the start handle when the start handle is selected
      const isDraggingEnd = selectedHandleIndex !== 0;
      const startProperties = { ...dragMarker, id: getMarkerId(segmentId, 0), isStart: true, isEnd: false, pathIndex: 0, endPathIndex: undefined };
      const endProperties = {
        ...dragMarker,
        id: getMarkerId(segmentId, endPathIndex! + 1),
        isStart: false,
        isEnd: true,
        pathIndex: endPathIndex!,
        endPathIndex: undefined
      };

      // Since the main layer filters out the drag marker we need to render both the handle being dragged and the remaining handle that would result after splitting the combined marker
      points.push(
        point<MarkerProperties>([longitude, latitude], {
          ...(isDraggingEnd ? endProperties : startProperties),
          latitude,
          longitude
        })
      );

      if (selectedPoint) {
        points.push(
          point<MarkerProperties>([selectedPoint[1], selectedPoint[0]], {
            ...(isDraggingEnd ? startProperties : endProperties),
            latitude: selectedPoint[0],
            longitude: selectedPoint[1]
          })
        );
      }
    } else {
      points.push(point<MarkerProperties>([longitude, latitude], dragMarker));
    }

    return featureCollection<Point, MarkerProperties>(points);
  }, [dragMarker, selectedHandleIndex, selectedPoint]);

  const data = useMemo(() => {
    // If there is no active segment, show markers for all segments
    const segments = activeSegmentId === undefined ? allSegments : allSegments.filter(segment => segment.id === activeSegmentId);
    const markers = segments
      .map(segment => {
        const { id, paths } = segment;

        if (!paths || paths.length === 0) {
          return [];
        }

        const points = paths.map(path => path.points[0]);
        const lastPath = paths[paths.length - 1];
        const lastPoint = lastPath.points[lastPath.points.length - 1];

        // If the segment has more than one point we need to add the last point to allow drawing the end handle
        if (paths[0].points.length > 1) {
          points.push(lastPoint);
        }

        // If there is a pending handle, insert it into the points array at the correct index
        if (pendingHandle) {
          // If the pending handle is at the end of the segment, we need to insert it after the last point since we added the previous last point above
          const index = pendingHandle.index === points.length - 1 ? pendingHandle.index + 1 : pendingHandle.index;
          points.splice(index, 0, [pendingHandle.latitude, pendingHandle.longitude]);
        }

        const startIndex = 0;
        const startPoint = points[startIndex];
        const endIndex = points.length - 1;
        const endPoint = points[endIndex];
        const isStartEnd = points.length > 1 && isEqual(startPoint, endPoint);
        // The end point is in the same path as the point before it
        const endPathIndex = endIndex - 1;

        const start = point<MarkerProperties>([startPoint[1], startPoint[0]], {
          id: getMarkerId(id, startIndex),
          segmentId: id,
          isStart: true,
          pathIndex: startIndex,
          latitude: startPoint[0],
          longitude: startPoint[1]
        });

        // Only set end properties on the start marker if it is a combined start/end marker
        if (isStartEnd) {
          start.properties.isEnd = true;
          start.properties.endPathIndex = endPathIndex;
        }

        // If there is only one point, we only need the start marker
        if (points.length === 1) {
          return [start];
        }

        const handles = points.slice(1, -1).map(([latitude, longitude], indexFromOne) => {
          const pathIndex = indexFromOne + 1; // Adjust index to match the points array including the start point

          // log analytics when a handle is added
          if (pendingHandle) {
            logCustomRouteUpdated({ entry_point_location: 'add handle' });
          }

          return point<MarkerProperties>([longitude, latitude], {
            id: getMarkerId(id, pathIndex),
            segmentId: id,
            pathIndex,
            latitude,
            longitude
          });
        });

        // If the start and end points are the same, we don't need an end marker
        if (isStartEnd) {
          return [start, ...handles];
        }

        const end = point<MarkerProperties>([endPoint[1], endPoint[0]], {
          id: getMarkerId(id, endIndex),
          segmentId: id,
          isEnd: true,
          pathIndex: endPathIndex,
          latitude: endPoint[0],
          longitude: endPoint[1]
        });

        return [start, ...handles, end];
      })
      .flat();

    return featureCollection<Point, MarkerProperties>(markers);
  }, [activeSegmentId, allSegments, pendingHandle]);

  const selectedMarker = useMemo(() => {
    if (selectedHandleIndex === undefined || selectedHandleIndex > data.features.length) {
      return undefined;
    }

    // If the selected handle index is one more than the number of features it means a combined start/end marker is selected
    const markerIndex = selectedHandleIndex === data.features.length ? 0 : selectedHandleIndex;
    return data.features[markerIndex].properties as MarkerProperties;
  }, [data, selectedHandleIndex]);

  const onClose = useCallback(() => {
    setIsContextMenuOpen(false);
  }, [setIsContextMenuOpen]);

  // Close the context menu when state affecting the markers changes
  useEffect(() => {
    setIsContextMenuOpen(false);
  }, [activeSegmentId, allSegments, isEditingWaypoint, setIsContextMenuOpen]);

  // Register event handler for clicking on markers
  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const onMarkerClick = (event: MapMouseEvent) => {
      if (!event.features) {
        return;
      }

      const { segmentId, isStart, isEnd, pathIndex, endPathIndex } = event.features[0].properties as MarkerProperties;
      // The endPathIndex will be defined if both isStart and isEnd are true
      const selectedPathIndex = isStart && isEnd ? endPathIndex! : pathIndex;
      // The handle index is one more than the path index for the end handle since it is at the end of the last path
      const newSelectedHandleIndex = isEnd ? selectedPathIndex + 1 : selectedPathIndex;

      const isHandleReselected = selectedHandleIndexRef.current === newSelectedHandleIndex;
      const isStartEndReselected = isStart && isEnd && (selectedHandleIndexRef.current === 0 || selectedHandleIndexRef.current === endPathIndex! + 1);

      if (isHandleReselected || isStartEndReselected) {
        if (!isContextMenuOpenRef.current) {
          setIsContextMenuOpen(true);
        }
        logCustomRouteContextualEditMenuViewed();
        return;
      }

      if (activeSegmentId !== segmentId) {
        dispatch(setActiveSegmentId({ activeSegmentId: segmentId, canEdit: true }));
      }

      dispatch(setSelectedHandleIndex(newSelectedHandleIndex));
    };

    registerEventHandler(customRouteMarkersId, 'click', onMarkerClick);

    return () => {
      unregisterEventHandler(customRouteMarkersId, 'click', onMarkerClick);
    };
  }, [
    isEditing,
    activeSegmentId,
    selectedHandleIndexRef,
    isContextMenuOpenRef,
    setIsContextMenuOpen,
    dispatch,
    registerEventHandler,
    unregisterEventHandler
  ]);

  return (
    <>
      <Source id={SOURCE_ID} type="geojson" data={data}>
        <Layer id={customRouteMarkersId} source={SOURCE_ID} type="symbol" layout={layout} {...filter} beforeId={beforeId} />
      </Source>
      <Source id={DRAG_SOURCE_ID} type="geojson" data={dragData}>
        <Layer id={customRouteMarkerDragId} source={DRAG_SOURCE_ID} type="symbol" layout={layout} beforeId={beforeId} />
      </Source>
      {isEditing && !isEditingWaypoint && !dragMarker && selectedMarker && (
        <PulsingMarker
          beforeId={beforeId}
          activeSegmentId={activeSegmentId}
          latitude={selectedMarker.latitude}
          longitude={selectedMarker.longitude}
        />
      )}
      {isContextMenuOpen && selectedMarker && <ContextMenu {...selectedMarker} onClose={onClose} />}
    </>
  );
};

export default Markers;
